/**
 * MeasurementsEditor Component
 *
 * Manages measurement definitions for an inspection template item.
 * Each measurement has a label, unit, and optional min/warningMin/max thresholds.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Stack,
  Chip,
  Collapse,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { MeasurementDefinition } from '@/hooks/useInspections';
import { useTranslation } from 'react-i18next';

interface MeasurementsEditorProps {
  measurements: MeasurementDefinition[];
  onChange: (measurements: MeasurementDefinition[]) => void;
}

function generateId(): string {
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function MeasurementsEditor({ measurements, onChange }: MeasurementsEditorProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const [expanded, setExpanded] = useState(measurements.length > 0);

  const handleAdd = () => {
    const newMeasurement: MeasurementDefinition = {
      id: generateId(),
      label: '',
      unit: '',
      sortOrder: measurements.length,
    };
    onChange([...measurements, newMeasurement]);
    setExpanded(true);
  };

  const handleUpdate = (index: number, field: keyof MeasurementDefinition, value: string | number | undefined) => {
    const updated = [...measurements];
    const item = updated[index];
    if (!item) return;
    updated[index] = { ...item, [field]: value };
    onChange(updated);
  };

  const handleDelete = (index: number) => {
    onChange(
      measurements
        .filter((_, i) => i !== index)
        .map((m, idx) => ({ ...m, sortOrder: idx }))
    );
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          {t('inspectionTemplates.measurements')}
        </Typography>
        {measurements.length > 0 && (
          <Chip size="small" label={measurements.length} variant="outlined" />
        )}
        {measurements.length > 0 && (
          <Tooltip title={expanded ? tc('collapse') : tc('expand')}>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ p: 0.25 }}
              aria-label={expanded ? tc('collapse') : tc('expand')}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Collapse in={expanded}>
        <Stack spacing={1}>
          {measurements.map((measurement, index) => (
            <Box
              key={measurement.id}
              display="flex"
              gap={1}
              alignItems="flex-start"
              sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}
            >
              <Stack spacing={1} flex={1}>
                <Box display="flex" gap={1}>
                  <TextField
                    label={t('inspectionTemplates.measurementLabel')}
                    value={measurement.label}
                    onChange={(e) => handleUpdate(index, 'label', e.target.value)}
                    size="small"
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    label={t('inspectionTemplates.measurementUnit')}
                    value={measurement.unit}
                    onChange={(e) => handleUpdate(index, 'unit', e.target.value)}
                    size="small"
                    sx={{ width: 80 }}
                    placeholder="mm"
                  />
                </Box>
                <Box display="flex" gap={1}>
                  <TextField
                    label={t('inspectionTemplates.measurementMin')}
                    type="number"
                    value={measurement.min ?? ''}
                    onChange={(e) =>
                      handleUpdate(index, 'min', e.target.value ? Number(e.target.value) : undefined)
                    }
                    size="small"
                    sx={{ width: 80 }}
                  />
                  <TextField
                    label={t('inspectionTemplates.warningMin')}
                    type="number"
                    value={measurement.warningMin ?? ''}
                    onChange={(e) =>
                      handleUpdate(index, 'warningMin', e.target.value ? Number(e.target.value) : undefined)
                    }
                    size="small"
                    sx={{ width: 100 }}
                  />
                  <TextField
                    label={t('inspectionTemplates.measurementMax')}
                    type="number"
                    value={measurement.max ?? ''}
                    onChange={(e) =>
                      handleUpdate(index, 'max', e.target.value ? Number(e.target.value) : undefined)
                    }
                    size="small"
                    sx={{ width: 80 }}
                  />
                </Box>
              </Stack>
              <Tooltip title={tc('delete')}>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(index)}
                  color="error"
                  aria-label={tc('delete')}
                  sx={{ minWidth: 44, minHeight: 44, mt: 0.5 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      </Collapse>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAdd}
        sx={{ mt: 0.5 }}
      >
        {t('inspectionTemplates.addMeasurement')}
      </Button>
    </Box>
  );
}
